// Top bar for the processing console: brand, status, notice, action buttons.
import { X, Ban, Minus, ArrowLeft } from "lucide-react";
import React from "react";

import { ConsoleLogo } from "./ConsoleParts";

import type { PipelineRun } from "./processingPipeline";

export function ConsoleTopBar({
  variantLabel,
  statusLabel,
  statusTone,
  run,
  notice,
  topBarExtra,
  onMinimize,
  onBack,
  onCancel,
  footer,
}: {
  variantLabel?: string;
  statusLabel: string;
  statusTone: { dot: string; text: string };
  run: PipelineRun;
  notice?: { title: string; detail?: string } | null;
  topBarExtra?: React.ReactNode;
  onMinimize?: () => void;
  onBack?: () => void;
  onCancel?: () => void;
  footer?: React.ReactNode;
}) {
  return (
    <header className="relative z-10 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 sm:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <ConsoleLogo />
        <div className="min-w-0">
          <div className="truncate text-sm font-bold leading-tight whitespace-nowrap">
            {variantLabel || "Document knowledge Enrichment"}
          </div>
        </div>
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-3 sm:gap-4">
        {notice && (
          <div className="flex min-w-0 max-w-sm items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 dark:border-red-500/20 dark:bg-red-500/10">
            <Ban className="mt-0.5 size-3.5 flex-none text-red-600 dark:text-red-400" />
            <div className="min-w-0">
              <div className="break-words text-xs font-bold text-red-700 dark:text-red-400">
                {notice.title}
              </div>
              {notice.detail && (
                <div className="break-words text-[11px] leading-snug text-red-700/90 dark:text-red-400/90">
                  {notice.detail}
                </div>
              )}
            </div>
          </div>
        )}
        {topBarExtra}
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            title="Keep running in the background"
            className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
          >
            <Minus className="size-3.5" />

          </button>
        )}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            title="Back to file picker"
            aria-label="Back to file picker"
            className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
          >
            <ArrowLeft className="size-3.5" />
            Back
          </button>
        )}
        {onCancel &&
          (footer ? (
            <button
              type="button"
              onClick={onCancel}
              title={run.done ? "Close" : "Cancel"}
              aria-label={run.done ? "Close" : "Cancel"}
              className="grid size-8 flex-none place-items-center rounded-lg border border-border text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
            >
              <X className="size-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
            >
              <X className="size-3.5" />
              {run.done ? "Close" : "Cancel"}
            </button>
          ))}
      </div>
    </header>
  );
}
